import { Loadable, LoadableState, LoadableStateLoaded } from '@eih-netbroker/common';
import { useTheme } from '../../../context/NbThemeContext';
import { NbIndefiniteLoader, NbIndefiniteLoaderProps } from '../loader/Loader';

const defaultErrorComponent = '-';
const defaultUninitializedComponent = '-';
const defaultLoaderProps: NbIndefiniteLoaderProps = { size: 100, isTransparent: false, isContrast: true };
const defaultLoaderPropsDarkMode: NbIndefiniteLoaderProps = { size: 100, isTransparent: true, isContrast: false };

type NbIndefiniteLoaderValuePropsWrapper<T> = {
  loadableValue: LoadableState<T>;
  loadedValueCb: (value: LoadableStateLoaded<T>) => React.ReactNode;
  loaderProps?: NbIndefiniteLoaderProps;
  errorComponent?: React.ReactNode;
  uninitializedComponent?: React.ReactNode;
  showLoaderOnUninitialized?: boolean;
};

export const NbIndefiniteLoaderValueWrapper = <T,>({
  loadableValue,
  loadedValueCb,
  loaderProps,
  errorComponent = defaultErrorComponent,
  uninitializedComponent = defaultUninitializedComponent,
  showLoaderOnUninitialized = false,
}: NbIndefiniteLoaderValuePropsWrapper<T>): React.ReactNode => {
  const { isDark } = useTheme();

  if (Loadable.isUninitialized(loadableValue) && !showLoaderOnUninitialized) {
    return <>{uninitializedComponent}</>;
  }

  if (loadableValue.isLoading || (Loadable.isUninitialized(loadableValue) && showLoaderOnUninitialized)) {
    return (
      <NbIndefiniteLoader
        {...(isDark ? { ...defaultLoaderPropsDarkMode, ...loaderProps } : { ...defaultLoaderProps, ...loaderProps })}
      />
    );
  }

  if (loadableValue.hasError) {
    return <>{errorComponent}</>;
  }

  if (Loadable.hasValue(loadableValue)) {
    return <>{loadedValueCb(loadableValue)}</>;
  }
};
